import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../locales/i18n';
import './TradeGrid.css';
import { calculateNDFSettlement, NDFCalculationInput } from '../../services/ndfCalculator';
import { exportTradeToExcel } from '../../utils/excelExport';
import { formatAmountDisplay, formatPriceDisplay, parseAmountInput } from '../../utils/amountFormatter';

interface ToleranceSettings {
  currencyTolerances: { currency: string; tolerance: number }[];
  defaultTolerance: number;
}

interface OurExpiryCalcGridProps {
  selectedTrades: any[];
  onCalculationsChanged?: (calculations: any[]) => void;
  cptyCalculations?: any[];
  toleranceSettings?: ToleranceSettings | null;
}

interface EditedValue {
  original: number;
  new: number;
}

const OurExpiryCalcGrid: React.FC<OurExpiryCalcGridProps> = ({ selectedTrades, onCalculationsChanged, cptyCalculations = [], toleranceSettings }) => {
  const { t } = useTranslation();
  const [editedFixingPrices, setEditedFixingPrices] = useState<{ [key: string]: EditedValue }>({});
  const [editedSettlementPrices, setEditedSettlementPrices] = useState<{ [key: string]: EditedValue }>({});
  const [editedDirections, setEditedDirections] = useState<{ [key: string]: { original: string; new: string } }>({});
  const [editedTradedAmounts, setEditedTradedAmounts] = useState<{ [key: string]: EditedValue }>({});
  const [editedPrices, setEditedPrices] = useState<{ [key: string]: EditedValue }>({});

  // Store original calculated values before any edits
  const [originalCalculations, setOriginalCalculations] = useState<{ [key: string]: { wePay: number; weReceive: number } }>({});

  // Fixing detail popover for Asiático trades
  const [fixingDetail, setFixingDetail] = useState<{ trade: any; position: { x: number; y: number } } | null>(null);
  const fixingDetailRef = useRef<HTMLDivElement>(null);

  // Undo history stack
  const [undoStack, setUndoStack] = useState<Array<{
    editedDirections: typeof editedDirections;
    editedTradedAmounts: typeof editedTradedAmounts;
    editedPrices: typeof editedPrices;
    editedFixingPrices: typeof editedFixingPrices;
    editedSettlementPrices: typeof editedSettlementPrices;
  }>>([]);

  // Process selected trades to add calculated fields
  const processedTrades = useMemo(() => {
    return selectedTrades.map(trade => {
      // Check if we have edited values
      const hasEdits = editedDirections[trade.TradeNumber] ||
                       editedTradedAmounts[trade.TradeNumber] ||
                       editedPrices[trade.TradeNumber] ||
                       editedFixingPrices[trade.TradeNumber] ||
                       editedSettlementPrices[trade.TradeNumber];

      const isPhysicalDelivery = trade.SettlementType !== 'Compensación';

      // If no edits and we have DB settlement amounts, use them
      if (!hasEdits && trade.WePayAmount !== undefined && trade.WeReceiveAmount !== undefined) {
        // Store original DB values if we don't have them yet
        if (!originalCalculations[trade.TradeNumber]) {
          setOriginalCalculations(prev => ({
            ...prev,
            [trade.TradeNumber]: {
              wePay: trade.WePayAmount || 0,
              weReceive: trade.WeReceiveAmount || 0
            }
          }));
        }

        // Return trade with DB settlement amounts as-is
        return {
          ...trade,
          isPhysicalDelivery,
          hasEdits: false
        };
      }

      // Get edited values or use originals
      const direction = editedDirections[trade.TradeNumber]?.new || trade.Direction;
      const quantityCurrency1 = editedTradedAmounts[trade.TradeNumber]?.new || trade.QuantityCurrency1;
      const price = editedPrices[trade.TradeNumber]?.new || trade.Price;

      // Physical delivery (Entrega Física) — fixed cashflows, no NDF formula
      if (trade.SettlementType !== 'Compensación') {
        const currency2Amount = quantityCurrency1 * price;
        const isBuy = direction === 'Buy';

        return {
          ...trade,
          isPhysicalDelivery: true,
          WePayCurrency: isBuy ? trade.Currency2 : trade.Currency1,
          WePayAmount: isBuy ? currency2Amount : quantityCurrency1,
          WeReceiveCurrency: isBuy ? trade.Currency1 : trade.Currency2,
          WeReceiveAmount: isBuy ? quantityCurrency1 : currency2Amount,
          hasEdits
        };
      }

      // NDF (Compensación) — calculate via NDF formula
      const fixingPrice = editedFixingPrices[trade.TradeNumber]?.new || trade.DisplayFixingPrice || trade.FixingRate || 0;
      const settlementPrice = editedSettlementPrices[trade.TradeNumber]?.new || trade.SettlementPrice || 0;

      try {
        const calculationInput: NDFCalculationInput = {
          direction,
          currency1: trade.Currency1,
          quantityCurrency1,
          price,
          currency2: trade.Currency2,
          quantityCurrency2: trade.QuantityCurrency1 * price,
          settlementType: trade.SettlementType,
          settlementCurrency: trade.SettlementCurrency,
          fixingPrice,
          settlementPrice
        };

        const result = calculateNDFSettlement(calculationInput);

        return {
          ...trade,
          isPhysicalDelivery: false,
          WePayCurrency: result.wePayCurrency,
          WePayAmount: Math.abs(result.wePay),
          WeReceiveCurrency: result.weReceiveCurrency,
          WeReceiveAmount: result.weReceive,
          hasEdits
        };
      } catch (error) {
        console.error(`Error calculating settlement for trade ${trade.TradeNumber}:`, error);
        return {
          ...trade,
          isPhysicalDelivery: false,
          WePayCurrency: trade.SettlementCurrency,
          WePayAmount: 0,
          WeReceiveCurrency: trade.SettlementCurrency,
          WeReceiveAmount: 0,
          hasEdits
        };
      }
    });
  }, [selectedTrades, editedDirections, editedTradedAmounts, editedPrices, editedFixingPrices, editedSettlementPrices, originalCalculations]);

  // Notify parent when calculations change
  useEffect(() => {
    if (onCalculationsChanged) {
      onCalculationsChanged(processedTrades);
    }
  }, [processedTrades, onCalculationsChanged]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    if (parts[0].length === 4) {
      // YYYY-MM-DD → display as DD-MM-YYYY
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return `${parts[0]}-${parts[1]}-${parts[2]}`;
  };

  const locale = i18n.language?.substring(0, 2) || 'es';

  const formatAmount = (value: number | null | undefined) => {
    if (value == null || value === 0) return '—';
    return formatAmountDisplay(value, locale, 2);
  };

  const formatCcyAmount = (ccy: string, value: number | null | undefined) => {
    if (value == null || value === 0) return '—';
    return `${ccy} ${formatAmount(value)}`;
  };

  const formatPrice = (value: number) => {
    return formatPriceDisplay(value, locale, 6);
  };

  // Save current state to undo stack
  const saveToUndoStack = () => {
    setUndoStack(prev => [...prev, {
      editedDirections: { ...editedDirections },
      editedTradedAmounts: { ...editedTradedAmounts },
      editedPrices: { ...editedPrices },
      editedFixingPrices: { ...editedFixingPrices },
      editedSettlementPrices: { ...editedSettlementPrices }
    }]);
  };

  // Undo function
  const handleUndo = () => {
    if (undoStack.length > 0) {
      const previousState = undoStack[undoStack.length - 1];
      setEditedDirections(previousState.editedDirections);
      setEditedTradedAmounts(previousState.editedTradedAmounts);
      setEditedPrices(previousState.editedPrices);
      setEditedFixingPrices(previousState.editedFixingPrices);
      setEditedSettlementPrices(previousState.editedSettlementPrices);
      setUndoStack(prev => prev.slice(0, -1));
    }
  };

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undoStack]);

  // Sync row heights between grids
  useEffect(() => {
    const syncRowHeights = () => {
      processedTrades.forEach((trade) => {
        const ourRow = document.getElementById(`our-row-${trade.TradeNumber}`);
        const cptyRow = document.getElementById(`cpty-row-${trade.TradeNumber}`);

        if (ourRow && cptyRow) {
          const ourHeight = ourRow.offsetHeight;
          cptyRow.style.height = `${ourHeight}px`;
          cptyRow.style.minHeight = `${ourHeight}px`;
        }
      });
    };

    // Sync after render with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(syncRowHeights, 50);

    // Sync on window resize
    window.addEventListener('resize', syncRowHeights);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', syncRowHeights);
    };
  }, [processedTrades, cptyCalculations, editedDirections, editedTradedAmounts, editedPrices, editedFixingPrices, editedSettlementPrices]);

  const handleFixingPriceChange = (tradeNumber: string, currentValue: number, inputValue: string) => {
    const newValue = parseAmountInput(inputValue);
    if (newValue !== null) {
      saveToUndoStack();
      if (newValue === currentValue) {
        // Value changed back to original, remove the edit
        setEditedFixingPrices(prev => {
          const newState = { ...prev };
          delete newState[tradeNumber];
          return newState;
        });
      } else {
        setEditedFixingPrices(prev => ({
          ...prev,
          [tradeNumber]: { original: currentValue || 0, new: newValue }
        }));
      }
    }
  };

  const handleSettlementPriceChange = (tradeNumber: string, currentValue: number, inputValue: string) => {
    const newValue = parseAmountInput(inputValue);
    if (newValue !== null) {
      saveToUndoStack();
      if (newValue === currentValue) {
        // Value changed back to original, remove the edit
        setEditedSettlementPrices(prev => {
          const newState = { ...prev };
          delete newState[tradeNumber];
          return newState;
        });
      } else {
        setEditedSettlementPrices(prev => ({
          ...prev,
          [tradeNumber]: { original: currentValue || 0, new: newValue }
        }));
      }
    }
  };

  const handleDirectionChange = (tradeNumber: string, currentValue: string, inputValue: string) => {
    if (inputValue) {
      saveToUndoStack();
      if (inputValue === currentValue) {
        // Value changed back to original, remove the edit
        setEditedDirections(prev => {
          const newState = { ...prev };
          delete newState[tradeNumber];
          return newState;
        });
      } else {
        setEditedDirections(prev => ({
          ...prev,
          [tradeNumber]: { original: currentValue, new: inputValue }
        }));
      }
    }
  };

  const handleTradedAmountChange = (tradeNumber: string, currentValue: number, inputValue: string) => {
    const newValue = parseAmountInput(inputValue);
    if (newValue !== null) {
      saveToUndoStack();
      if (newValue === currentValue) {
        // Value changed back to original, remove the edit
        setEditedTradedAmounts(prev => {
          const newState = { ...prev };
          delete newState[tradeNumber];
          return newState;
        });
      } else {
        setEditedTradedAmounts(prev => ({
          ...prev,
          [tradeNumber]: { original: currentValue || 0, new: newValue }
        }));
      }
    }
  };

  const handlePriceChange = (tradeNumber: string, currentValue: number, inputValue: string) => {
    const newValue = parseAmountInput(inputValue);
    if (newValue !== null) {
      saveToUndoStack();
      if (newValue === currentValue) {
        // Value changed back to original, remove the edit
        setEditedPrices(prev => {
          const newState = { ...prev };
          delete newState[tradeNumber];
          return newState;
        });
      } else {
        setEditedPrices(prev => ({
          ...prev,
          [tradeNumber]: { original: currentValue || 0, new: newValue }
        }));
      }
    }
  };

  const renderEditableValue = (
    tradeNumber: string,
    value: number,
    editedValues: { [key: string]: EditedValue },
    onBlur: (tradeNumber: string, currentValue: number, inputValue: string) => void,
    formatFn: (value: number) => string = formatAmount
  ) => {
    const edited = editedValues[tradeNumber];
    if (edited) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <span style={{ textDecoration: 'line-through', color: '#888' }}>
            {formatFn(edited.original)}
          </span>
          <input
            type="text"
            defaultValue={formatFn(edited.new)}
            onBlur={(e) => onBlur(tradeNumber, edited.original, e.target.value)}
            style={{
              width: '100%',
              backgroundColor: 'transparent',
              border: 'none',
              color: 'inherit',
              textAlign: 'right',
              fontSize: '12px',
              fontWeight: 'inherit',
              padding: 0,
              outline: 'none'
            }}
          />
        </div>
      );
    }
    return (
      <input
        type="text"
        defaultValue={formatFn(value)}
        onBlur={(e) => onBlur(tradeNumber, value, e.target.value)}
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          border: 'none',
          color: 'inherit',
          textAlign: 'right',
          fontSize: '12px',
          fontWeight: 'inherit',
          padding: 0,
          outline: 'none'
        }}
      />
    );
  };

  const renderEditableDirection = (
    tradeNumber: string,
    value: string,
    editedValues: { [key: string]: { original: string; new: string } },
    onChange: (tradeNumber: string, currentValue: string, inputValue: string) => void
  ) => {
    const edited = editedValues[tradeNumber];
    if (edited) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ textDecoration: 'line-through', color: '#888' }}>
            {edited.original}
          </span>
          <select
            defaultValue={edited.new}
            onChange={(e) => onChange(tradeNumber, edited.original, e.target.value)}
            style={{
              width: '100%',
              backgroundColor: 'transparent',
              border: 'none',
              color: 'inherit',
              textAlign: 'center',
              fontSize: '12px',
              fontWeight: 'inherit',
              padding: 0,
              outline: 'none'
            }}
          >
            <option value="Buy" style={{ backgroundColor: '#1e1e1e', color: '#fff' }}>Buy</option>
            <option value="Sell" style={{ backgroundColor: '#1e1e1e', color: '#fff' }}>Sell</option>
          </select>
        </div>
      );
    }
    return (
      <select
        defaultValue={value}
        onChange={(e) => onChange(tradeNumber, value, e.target.value)}
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          border: 'none',
          color: 'inherit',
          textAlign: 'center',
          fontSize: '12px',
          fontWeight: 'inherit',
          padding: 0,
          outline: 'none'
        }}
      >
        <option value="Buy" style={{ backgroundColor: '#1e1e1e', color: '#fff' }}>Buy</option>
        <option value="Sell" style={{ backgroundColor: '#1e1e1e', color: '#fff' }}>Sell</option>
      </select>
    );
  };

  const handleExportRow = async (trade: any) => {
    // Find matching counterparty calculation
    const cptyTrade = cptyCalculations.find(c => c.TradeNumber === trade.TradeNumber);

    if (!cptyTrade) {
      alert('Counterparty calculation not found for this trade');
      return;
    }

    // Use original values for export (no edits)
    const originalOurTrade = selectedTrades.find(t => t.TradeNumber === trade.TradeNumber);
    if (!originalOurTrade) {
      alert('Original trade data not found');
      return;
    }

    // Export with original values only
    await exportTradeToExcel(originalOurTrade, cptyTrade);
  };

  // Helper: determine if an input field differs from counterparty (FR-7.2.7.5)
  const getInputDiffStyle = (tradeNumber: string, field: string, ourValue: any): React.CSSProperties => {
    const cptyCalc = cptyCalculations.find(c => c.TradeNumber === tradeNumber);
    if (!cptyCalc) return {};

    let cptyValue: any;
    switch (field) {
      case 'Direction': cptyValue = cptyCalc.Direction; break;
      case 'Amount': cptyValue = cptyCalc.QuantityCurrency1; break;
      case 'Price': cptyValue = cptyCalc.Price; break;
      case 'FixingPrice': cptyValue = cptyCalc.FixingPrice || cptyCalc.DisplayFixingPrice || cptyCalc.FixingRate || 0; break;
      case 'SettlementPrice': cptyValue = cptyCalc.SettlementPrice || 0; break;
      default: return {};
    }

    // For numeric fields, compare with tolerance
    if (typeof ourValue === 'number' && typeof cptyValue === 'number') {
      if (Math.abs(ourValue - cptyValue) > 0.001) {
        return { color: '#dc3545', fontWeight: 'bold' };
      }
    } else if (ourValue !== cptyValue) {
      return { color: '#dc3545', fontWeight: 'bold' };
    }

    return {};
  };

  // Right-click on Fixing Price cell for Asiático trades shows fixing schedule
  const handleFixingPriceContextMenu = (e: React.MouseEvent, trade: any) => {
    if (!trade.IsAverageRateForward) return;
    e.preventDefault();
    setFixingDetail({ trade, position: { x: e.clientX, y: e.clientY } });
  };

  // Dismiss fixing detail popover on click-outside or Escape
  useEffect(() => {
    if (!fixingDetail) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (fixingDetailRef.current && !fixingDetailRef.current.contains(e.target as Node)) {
        setFixingDetail(null);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFixingDetail(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [fixingDetail]);

  // Position fixing detail popover within viewport
  useEffect(() => {
    if (!fixingDetail || !fixingDetailRef.current) return;
    const el = fixingDetailRef.current;
    const rect = el.getBoundingClientRect();
    const { innerWidth, innerHeight } = window;
    if (fixingDetail.position.x + rect.width > innerWidth) {
      el.style.left = `${fixingDetail.position.x - rect.width}px`;
    }
    if (fixingDetail.position.y + rect.height > innerHeight) {
      el.style.top = `${fixingDetail.position.y - rect.height}px`;
    }
  }, [fixingDetail]);

  // Format YYYY-MM-DD date to DD/MM/YYYY for fixing schedule display
  const formatFixingDate = (dateStr: string) => {
    if (!dateStr) return '';
    if (dateStr.includes('/')) return dateStr;
    const parts = dateStr.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  if (processedTrades.length === 0) {
    return (
      <div style={{ flex: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b3b3b3' }}>
        {t('forwardMaturity.selectTradesMessage')}
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '0.5rem' }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        color: '#fff',
        fontSize: '12px'
      }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', fontWeight: 600, width: '80px' }}>{t('forwardMaturity.columns.tradeNumber')}</th>
            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', fontWeight: 600, width: '100px' }}>{t('forwardMaturity.columns.product')}</th>
            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', fontWeight: 600, width: '140px' }}>{t('forwardMaturity.columns.counterparty')}</th>
            <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', fontWeight: 600, width: '90px' }}>{t('forwardMaturity.columns.maturityDate')}</th>
            <th style={{ textAlign: 'center', padding: '0.4rem 0.5rem', fontWeight: 600, width: '50px' }}>{t('forwardMaturity.columns.direction')}</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', fontWeight: 600, width: '140px' }}>{t('forwardMaturity.columns.tradedAmount')}</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', fontWeight: 600, width: '80px' }}>{t('forwardMaturity.columns.price')}</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', fontWeight: 600, width: '80px' }}>{t('forwardMaturity.columns.fixingPrice')}</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', fontWeight: 600, width: '80px' }}>{t('forwardMaturity.columns.settlementExchangeRate')}</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', fontWeight: 600, width: '140px' }}>{t('forwardMaturity.columns.pagamos')}</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', fontWeight: 600, width: '140px' }}>{t('forwardMaturity.columns.recibimos')}</th>
            <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', fontWeight: 600, width: '140px' }}>{t('forwardMaturity.columns.originalValue', 'Valor Original')}</th>
            <th style={{ textAlign: 'center', padding: '0.4rem 0.5rem', fontWeight: 600, width: '30px' }}></th>
          </tr>
        </thead>
        <tbody>
          {processedTrades.map((trade, index) => {
            // Get current effective values for input diff comparison
            const effectiveDirection = editedDirections[trade.TradeNumber]?.new || trade.Direction;
            const effectiveAmount = editedTradedAmounts[trade.TradeNumber]?.new || trade.QuantityCurrency1;
            const effectivePrice = editedPrices[trade.TradeNumber]?.new || trade.Price;
            const effectiveFixingPrice = editedFixingPrices[trade.TradeNumber]?.new || trade.DisplayFixingPrice || trade.FixingRate || 0;
            const effectiveSettlementPrice = editedSettlementPrices[trade.TradeNumber]?.new || trade.SettlementPrice || 0;

            const dirDiffStyle = getInputDiffStyle(trade.TradeNumber, 'Direction', effectiveDirection);
            const amtDiffStyle = getInputDiffStyle(trade.TradeNumber, 'Amount', effectiveAmount);
            const priceDiffStyle = getInputDiffStyle(trade.TradeNumber, 'Price', effectivePrice);
            const fixDiffStyle = getInputDiffStyle(trade.TradeNumber, 'FixingPrice', effectiveFixingPrice);
            const settDiffStyle = getInputDiffStyle(trade.TradeNumber, 'SettlementPrice', effectiveSettlementPrice);

            return (
            <tr key={trade.id || trade.TradeNumber} id={`our-row-${trade.TradeNumber}`} style={{
              borderBottom: '1px solid var(--border-color)',
              backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)'
            }}>
              <td style={{ padding: '0.4rem 0.5rem' }}>{trade.TradeNumber}</td>
              <td style={{ padding: '0.4rem 0.5rem', fontSize: '11px' }}>{trade.ProductLabel || 'Forward'}</td>
              <td style={{ padding: '0.4rem 0.5rem' }}>{trade.CounterpartyName}</td>
              <td style={{ padding: '0.4rem 0.5rem' }}>{formatDate(trade.MaturityDate)}</td>
              <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center', backgroundColor: trade.isPhysicalDelivery ? 'transparent' : 'rgba(100, 149, 237, 0.1)' }}>
                {trade.isPhysicalDelivery
                  ? <span>{trade.Direction}</span>
                  : renderEditableDirection(trade.TradeNumber, trade.Direction, editedDirections, handleDirectionChange)}
              </td>
              <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', backgroundColor: trade.isPhysicalDelivery ? 'transparent' : 'rgba(100, 149, 237, 0.1)' }}>
                {trade.isPhysicalDelivery ? (
                  <span>{trade.Currency1} {formatAmount(trade.QuantityCurrency1)}</span>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.25rem' }}>
                    <span>{trade.Currency1}</span>
                    {renderEditableValue(trade.TradeNumber, trade.QuantityCurrency1, editedTradedAmounts, handleTradedAmountChange, formatAmount)}
                  </div>
                )}
              </td>
              <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', backgroundColor: trade.isPhysicalDelivery ? 'transparent' : 'rgba(100, 149, 237, 0.1)' }}>
                {trade.isPhysicalDelivery
                  ? <span>{formatPrice(trade.Price)}</span>
                  : renderEditableValue(trade.TradeNumber, trade.Price, editedPrices, handlePriceChange, formatPrice)}
              </td>
              <td
                style={{ padding: '0.4rem 0.5rem', textAlign: 'right', backgroundColor: trade.isPhysicalDelivery ? 'transparent' : 'rgba(100, 149, 237, 0.1)', ...(trade.IsAverageRateForward ? { cursor: 'context-menu' } : {}) }}
                onContextMenu={(e) => handleFixingPriceContextMenu(e, trade)}
                onMouseDownCapture={(e) => {
                  if (e.button === 2 && trade.IsAverageRateForward) {
                    e.preventDefault();
                  }
                }}
              >
                {trade.isPhysicalDelivery
                  ? <span style={{ color: '#888' }}>N/A</span>
                  : renderEditableValue(trade.TradeNumber, trade.DisplayFixingPrice || trade.FixingRate || 0, editedFixingPrices, handleFixingPriceChange, formatPrice)}
              </td>
              <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', backgroundColor: trade.isPhysicalDelivery ? 'transparent' : 'rgba(100, 149, 237, 0.1)' }}>
                {trade.isPhysicalDelivery
                  ? <span style={{ color: '#888' }}>N/A</span>
                  : renderEditableValue(trade.TradeNumber, trade.SettlementPrice || 0, editedSettlementPrices, handleSettlementPriceChange, formatPrice)}
              </td>
              {(() => {
                // Check if trade has counterparty confirmation
                const isConfirmed = trade.expiryState && trade.expiryState !== 'pending';

                // Find matching counterparty calculation
                const cptyCalc = cptyCalculations.find(c => c.TradeNumber === trade.TradeNumber);
                const originalWePay = originalCalculations[trade.TradeNumber]?.wePay || 0;
                const originalWeReceive = originalCalculations[trade.TradeNumber]?.weReceive || 0;
                const hasEdits = trade.hasEdits;

                // Compare with counterparty calculations (only if confirmed)
                // Green = exact match, Orange = within tolerance, Red = exceeds tolerance
                const getToleranceForCcy = (ccy: string): number => {
                  if (toleranceSettings) {
                    const entry = toleranceSettings.currencyTolerances.find(e => e.currency === ccy);
                    if (entry) return entry.tolerance;
                    return toleranceSettings.defaultTolerance;
                  }
                  return ccy === 'CLP' ? 2 : 0.01;
                };
                const wePayTol = getToleranceForCcy(trade.WePayCurrency || '');
                const weRecvTol = getToleranceForCcy(trade.WeReceiveCurrency || '');
                const wePayDiff = isConfirmed && cptyCalc ? Math.abs(trade.WePayAmount - cptyCalc.WePayAmount) : Infinity;
                const weRecvDiff = isConfirmed && cptyCalc ? Math.abs(trade.WeReceiveAmount - cptyCalc.WeReceiveAmount) : Infinity;

                // Only apply color coding if confirmed
                const wePayBgColor = !isConfirmed ? 'transparent' : wePayDiff === 0 ? '#28a745' : wePayDiff <= wePayTol ? '#e67e00' : '#dc3545';
                const weReceiveBgColor = !isConfirmed ? 'transparent' : weRecvDiff === 0 ? '#28a745' : weRecvDiff <= weRecvTol ? '#e67e00' : '#dc3545';

                return (
                  <>
                    <td style={{
                      padding: '0.4rem 0.5rem',
                      textAlign: 'right',
                      backgroundColor: wePayBgColor,
                      color: isConfirmed ? '#fff' : 'inherit'
                    }}>
                      {hasEdits ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <span style={{ textDecoration: 'line-through', color: '#ddd' }}>
                            {formatCcyAmount(trade.WePayCurrency, originalWePay)}
                          </span>
                          <span>{formatCcyAmount(trade.WePayCurrency, trade.WePayAmount)}</span>
                        </div>
                      ) : (
                        <span>{formatCcyAmount(trade.WePayCurrency, trade.WePayAmount)}</span>
                      )}
                    </td>
                    <td style={{
                      padding: '0.4rem 0.5rem',
                      textAlign: 'right',
                      backgroundColor: weReceiveBgColor,
                      color: isConfirmed ? '#fff' : 'inherit'
                    }}>
                      {hasEdits ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <span style={{ textDecoration: 'line-through', color: '#ddd' }}>
                            {formatCcyAmount(trade.WeReceiveCurrency, originalWeReceive)}
                          </span>
                          <span>{formatCcyAmount(trade.WeReceiveCurrency, trade.WeReceiveAmount)}</span>
                        </div>
                      ) : (
                        <span>{formatCcyAmount(trade.WeReceiveCurrency, trade.WeReceiveAmount)}</span>
                      )}
                    </td>
                  </>
                );
              })()}
              {/* Original Value column — shows archived values when counterparty amounts were accepted */}
              {(() => {
                const orig = trade.originalSettlement;
                if (!orig) {
                  return <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: 'var(--text-secondary)' }}>—</td>;
                }
                // Show the non-zero original settlement leg
                const origPay = orig.WePayAmount || 0;
                const origRecv = orig.WeReceiveAmount || 0;
                const usePayLeg = origPay > origRecv;
                const amt = usePayLeg ? origPay : origRecv;
                const ccy = usePayLeg ? (orig.WePayCurrency || '') : (orig.WeReceiveCurrency || '');
                return (
                  <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: '#e67e00', fontStyle: 'italic' }}>
                    {ccy} {formatAmount(amt)}
                  </td>
                );
              })()}
              <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                {trade.expiryState && trade.expiryState !== 'pending' && (
                  <button
                    onClick={() => handleExportRow(trade)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.2rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title="Export to Excel"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{ color: '#28a745' }}>
                      <path d="M14 2H2a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1zM5.5 13H3V3h2.5v10zm4.5 0H6.5V3H10v10zm4 0h-3V3h3v10z"/>
                    </svg>
                  </button>
                )}
              </td>
            </tr>
            );
          })}
        </tbody>
        <tfoot>
          {(() => {
            // Group totals by currency for our calculations (all trades)
            const wePayTotals: { [currency: string]: number } = {};
            const weReceiveTotals: { [currency: string]: number } = {};
            const tradeCountByCurrency: { [currency: string]: number } = {};

            processedTrades.forEach(trade => {
              wePayTotals[trade.WePayCurrency] = (wePayTotals[trade.WePayCurrency] || 0) + trade.WePayAmount;
              weReceiveTotals[trade.WeReceiveCurrency] = (weReceiveTotals[trade.WeReceiveCurrency] || 0) + trade.WeReceiveAmount;
              if (trade.WePayCurrency) {
                tradeCountByCurrency[trade.WePayCurrency] = (tradeCountByCurrency[trade.WePayCurrency] || 0) + 1;
              }
              if (trade.WeReceiveCurrency && trade.WeReceiveCurrency !== trade.WePayCurrency) {
                tradeCountByCurrency[trade.WeReceiveCurrency] = (tradeCountByCurrency[trade.WeReceiveCurrency] || 0) + 1;
              }
            });

            // Group totals by currency for counterparty calculations (only confirmed trades)
            const cptyWePayTotals: { [currency: string]: number } = {};
            const cptyWeReceiveTotals: { [currency: string]: number } = {};

            cptyCalculations.forEach(trade => {
              // Only include confirmed trades in totals
              if (trade.expiryState && trade.expiryState !== 'pending') {
                if (trade.WePayCurrency && trade.WePayAmount !== undefined) {
                  cptyWePayTotals[trade.WePayCurrency] = (cptyWePayTotals[trade.WePayCurrency] || 0) + trade.WePayAmount;
                }
                if (trade.WeReceiveCurrency && trade.WeReceiveAmount !== undefined) {
                  cptyWeReceiveTotals[trade.WeReceiveCurrency] = (cptyWeReceiveTotals[trade.WeReceiveCurrency] || 0) + trade.WeReceiveAmount;
                }
              }
            });

            // Get all unique currencies from both We Pay and We Receive
            const allCurrencies = Array.from(new Set([...Object.keys(wePayTotals), ...Object.keys(weReceiveTotals)])).sort();

            return allCurrencies.flatMap((currency, i) => {
              const ourWePayTotal = wePayTotals[currency] || 0;
              const ourWeReceiveTotal = weReceiveTotals[currency] || 0;
              const cptyWePayTotal = cptyWePayTotals[currency] || 0;
              const cptyWeReceiveTotal = cptyWeReceiveTotals[currency] || 0;

              // Compare totals — only color-code when counterparty data exists
              const hasCptyData = cptyWePayTotal > 0 || cptyWeReceiveTotal > 0;

              let wePayBgColor = 'transparent';
              let weReceiveBgColor = 'transparent';

              if (hasCptyData) {
                const getToleranceForCcy = (ccy: string): number => {
                  if (toleranceSettings) {
                    const entry = toleranceSettings.currencyTolerances.find(e => e.currency === ccy);
                    if (entry) return entry.tolerance;
                    return toleranceSettings.defaultTolerance;
                  }
                  return ccy === 'CLP' ? 2 : 0.01;
                };
                const tolerance = getToleranceForCcy(currency) * (tradeCountByCurrency[currency] || 1);
                const wePayDiff = Math.abs(ourWePayTotal - cptyWePayTotal);
                const weRecvDiff = Math.abs(ourWeReceiveTotal - cptyWeReceiveTotal);

                wePayBgColor = wePayDiff === 0 ? '#28a745' : wePayDiff <= tolerance ? '#e67e00' : '#dc3545';
                weReceiveBgColor = weRecvDiff === 0 ? '#28a745' : weRecvDiff <= tolerance ? '#e67e00' : '#dc3545';
              }

              // Net = We Receive - We Pay (positive = we receive net, negative = we pay net)
              const netAmount = ourWeReceiveTotal - ourWePayTotal;

              return [
                <tr key={currency} style={{ borderTop: i === 0 ? '2px solid var(--border-color)' : '1px solid var(--border-color)', fontWeight: 'bold' }}>
                  <td colSpan={9} style={{ padding: '0.6rem 0.5rem', textAlign: 'right' }}>
                    {i === 0 ? 'Totals:' : ''}
                  </td>
                  <td style={{ padding: '0.6rem 0.5rem', textAlign: 'right', backgroundColor: wePayBgColor, color: hasCptyData ? '#fff' : 'inherit' }}>
                    {formatCcyAmount(currency, ourWePayTotal)}
                  </td>
                  <td style={{ padding: '0.6rem 0.5rem', textAlign: 'right', backgroundColor: weReceiveBgColor, color: hasCptyData ? '#fff' : 'inherit' }}>
                    {formatCcyAmount(currency, ourWeReceiveTotal)}
                  </td>
                  <td style={{ padding: '0.6rem 0.5rem' }}></td>
                  <td style={{ padding: '0.6rem 0.5rem' }}></td>
                </tr>,
                <tr key={`${currency}-net`} style={{ fontWeight: 'bold', fontStyle: 'italic' }}>
                  <td colSpan={9} style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: 'var(--text-secondary)', fontSize: '12px' }}>
                    {t('forwardMaturity.net')}:
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: netAmount < 0 ? '#dc3545' : 'transparent' }}>
                    {netAmount < 0 ? formatCcyAmount(currency, Math.abs(netAmount)) : ''}
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: netAmount >= 0 ? '#28a745' : 'transparent' }}>
                    {netAmount >= 0 ? formatCcyAmount(currency, netAmount) : ''}
                  </td>
                  <td style={{ padding: '0.4rem 0.5rem' }}></td>
                  <td style={{ padding: '0.4rem 0.5rem' }}></td>
                </tr>,
              ];
            });
          })()}
        </tfoot>
      </table>

      {/* Fixing detail popover for Asiático trades */}
      {fixingDetail && fixingDetail.trade.AverageRateFixingSchedule && (
        <div
          ref={fixingDetailRef}
          style={{
            position: 'fixed',
            left: fixingDetail.position.x,
            top: fixingDetail.position.y,
            zIndex: 9999,
            backgroundColor: '#2a2a2a',
            border: '1px solid #444',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
            padding: '12px 16px',
            fontFamily: "'Manrope', sans-serif",
            fontSize: '12px',
            color: '#e0e0e0',
            minWidth: '220px',
            animation: 'fadeIn 0.15s ease-out'
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '8px', color: '#fff', fontSize: '13px' }}>
            {t('forwardMaturity.fixingDetail.title')} — Trade {fixingDetail.trade.TradeNumber}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #444' }}>
                <th style={{ textAlign: 'left', padding: '4px 8px 4px 0', fontWeight: 600, color: '#999' }}>
                  {t('forwardMaturity.fixingDetail.date')}
                </th>
                <th style={{ textAlign: 'right', padding: '4px 0 4px 8px', fontWeight: 600, color: '#999' }}>
                  {t('forwardMaturity.fixingDetail.rate')}
                </th>
              </tr>
            </thead>
            <tbody>
              {(fixingDetail.trade.AverageRateFixingSchedule as any[]).map((entry: any, i: number) => (
                <tr key={i}>
                  <td style={{ padding: '3px 8px 3px 0' }}>
                    {formatFixingDate(entry.Date || entry.date || '')}
                  </td>
                  <td style={{ textAlign: 'right', padding: '3px 0 3px 8px' }}>
                    {entry.RealisedRate != null
                      ? formatPrice(entry.RealisedRate)
                      : <span style={{ color: '#888', fontStyle: 'italic' }}>{t('forwardMaturity.fixingDetail.pending')}</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '1px solid #444' }}>
                <td style={{ padding: '6px 8px 0 0', fontWeight: 'bold', color: '#fff' }}>
                  {t('forwardMaturity.fixingDetail.average')}
                </td>
                <td style={{ textAlign: 'right', padding: '6px 0 0 8px', fontWeight: 'bold', color: '#fff' }}>
                  {fixingDetail.trade.AverageFixingRate != null
                    ? (Math.round(fixingDetail.trade.AverageFixingRate * 100) / 100).toFixed(2)
                    : '—'
                  }
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default OurExpiryCalcGrid;
